import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Award, Lightbulb, Handshake, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { aboutUs } from '../mockData';
import { useLanguage } from '../i18n/LanguageContext';

const iconMap = { Leaf, Award, Lightbulb, Handshake };

const About = () => {
  const { t } = useLanguage();
  const whyItems = t('about.why');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="About Us"
        description="VB Global - 29+ years of excellence in industrial trading. ISO 9001:2015 certified company products. Leading supplier of eco-friendly paper chemicals, construction chemicals, SS wire mesh, and industrial equipment in Coimbatore, India."
        keywords="VB Global about, paper chemicals supplier history, industrial chemicals supplier Coimbatore, ISO 9001:2015 certified supplier, eco-friendly chemicals company, 29 years experience, Tamil Nadu industrial supplier, trusted teammate, sustainable industrial solutions"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 data-testid="about-hero-title" className="text-4xl md:text-5xl font-bold mb-6">
              {t('about.heroTitle')}
            </h1>
            <p className="text-xl text-gray-200">
              {t('about.heroSub')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="inline-block bg-[#8B2C3A] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                {t('about.ourMission')}
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.missionTitle')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {aboutUs.mission}
              </p>
            </div>
            <div>
              <div className="inline-block bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                {t('about.ourVision')}
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.visionTitle')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {aboutUs.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.valuesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('about.valuesSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutUs.values.map((value, index) => {
              const Icon = iconMap[value.icon];
              return (
                <Card key={index} className="border-2 hover:border-[#8B2C3A] transition-all hover:shadow-lg text-center">
                  <CardHeader>
                    <div className="w-16 h-16 bg-[#8B2C3A] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80" 
                alt="VB Global Team"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('about.whyTitle')}
              </h2>
              <div className="space-y-4">
                {whyItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('about.certTitle')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.certSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutUs.certifications.map((cert, index) => (
              <Card key={index} className="border-2 hover:border-emerald-600 transition-colors text-center">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-semibold text-gray-900">{cert}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {t('about.ctaSub')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
              {t('about.ctaButton')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
